import type { MapCloudPoint, MapPointCloud } from '@gpx-spa/geo-domain';
import type { DecodedShipmentTrack } from '@/utils/shipment-detail-map-decode';
import {
  lineColorHexForShipmentDevice,
  lineColorHexForShipmentStatus,
} from '@/map/operational/shipment-status-line-colors';
import type { ShipmentTrackVertexPayload } from '@/map/operational/shipment-track-vertex-payload';

export type TrackPointCloudDecodedRow = {
  device_id: number;
  decoded: DecodedShipmentTrack | null;
};

export type TrackPointCloudColorMode = 'per-device' | 'status';

/** Ring radius for a track point. Zero and missing accuracy draw no ring. */
export function positiveTrackAccuracyRadiusMeters(
  meters: number | null | undefined,
): number | undefined {
  if (typeof meters !== 'number' || !Number.isFinite(meters) || meters <= 0) {
    return undefined;
  }
  return meters;
}

/**
 * Build a point cloud from decoded shipment track rows (raw pings, not convoy segment vertices).
 */
export function buildTrackPointCloudFromDecodedRows(
  rows: readonly TrackPointCloudDecodedRow[],
  status: string | undefined,
  colorMode: TrackPointCloudColorMode,
): MapPointCloud {
  const points: MapCloudPoint[] = [];
  const sorted = [...rows].sort((a, b) => a.device_id - b.device_id);

  sorted.forEach((row, deviceIndex) => {
    const decoded = row.decoded;
    if (!decoded?.points?.length) return;

    const fillColor =
      colorMode === 'status'
        ? lineColorHexForShipmentStatus(status)
        : lineColorHexForShipmentDevice(status, deviceIndex);

    for (const p of decoded.points) {
      const payload: ShipmentTrackVertexPayload = {
        timestampSec: p.timestampSec,
        battery: p.battery,
        fixTypeCode: p.fixTypeCode,
        accuracyMeters: p.accuracyMeters,
      };
      points.push({
        position: { lat: p.location[1], lng: p.location[0] },
        style: {
          fillColor,
          borderColor: '#ffffff',
          radius: 6,
          borderWidth: 2,
        },
        accuracyRadiusMeters: positiveTrackAccuracyRadiusMeters(p.accuracyMeters),
        payload,
      });
    }
  });

  return { points };
}

/** Merge point clouds from multiple shipments (detail/list batch). */
export function mergeTrackPointClouds(
  clouds: readonly MapPointCloud[],
): MapPointCloud {
  const points: MapCloudPoint[] = [];
  for (const c of clouds) {
    points.push(...c.points);
  }
  return { points };
}
